import React, { useContext, useEffect, useState } from "react";
import { UserSessionContext } from "../context/UserSessionContext";
import { fetchUserDevices, registerDevice } from "../api/client";
import { LogoutButton } from "../components/LogoutButton";
import { DeviceTable } from "../components/DeviceTable";
import { AddDeviceForm } from "../components/AddDeviceForm";
import { HeatingDecisionChartDev } from "../components/DeviceHeatingChart";
import { EditDevice } from "../context/EditDevice";
import { RegisterDeviceInput, SessionDevice } from "../api/contracts/deviceTypes";

export const UserLandingPage: React.FC = () => {
  const { user, devices, setDevices } = useContext(UserSessionContext);

  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  /* -----------------------------
     Load devices on page entry
     ----------------------------- */

  const reloadDevices = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetchUserDevices();
      if (res.success) {
        setDevices(res.devices);
      } else {
        setError("Failed to load devices from server.");
      }
    } catch (err) {
      console.error(err);
      setError("Network or server error while fetching devices.");
    } finally {
      setLoading(false);
    }
  }, [setDevices]);

    useEffect(() => {
    if (!user) return;
    reloadDevices();
  }, [user, reloadDevices]);

  /* -----------------------------
     Register new device
     ----------------------------- */
  const handleRegister = async (device: RegisterDeviceInput) => {
    setLoading(true);
    setError(null);

    try {
      const updatedDevices: SessionDevice[] = await registerDevice(device);
      setDevices(updatedDevices);
      setShowAddForm(false);
    } catch (err) {
      console.error("Device registration failed:", err);
      setError("Failed to register device.");
    } finally {
      setLoading(false);
    }
  };

  const editingDevice = editingDeviceId
    ? devices.find(d => d.deviceId === editingDeviceId) ?? null
    : null;

  if (!user) {
    return <p>User session missing.</p>;
  }

  return (
    <div className="min-h-screen bg-gray-200 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div style={{ padding: "2rem" }}>
          {/* Header */}

          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-800">
              Welcome, {user.name}
            </h1>
            <LogoutButton />
          </div>

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {!loading && !showAddForm && (
            <>
              {devices.length === 0 ? (
                <p>No devices registered yet.</p>
              ) : (
                <>
                  <DeviceTable
                    devices={devices}
                    onSelectDevice={setSelectedDeviceId}
                    onEditDevice={(d) => setEditingDeviceId(d.deviceId)}
                  />

                  {editingDevice && (
                    <EditDevice
                      device={editingDevice}
                      onCancel={() => setEditingDeviceId(null)}
                      onSaved={reloadDevices}
                    />
                  )}

                  {selectedDeviceId && (
                    <div style={{ marginTop: "2rem" }}>
                      <HeatingDecisionChartDev deviceId={selectedDeviceId} />
                    </div>
                  )}
                </>
              )}

              <div style={{ marginTop: "1.5rem" }}>
                <button onClick={() => setShowAddForm(true)}>
                  Register new device
                </button>
              </div>
            </>
          )}

          {showAddForm && (
            <>
              <AddDeviceForm onRegister={handleRegister} loading={loading} />
              <button onClick={() => setShowAddForm(false)} style={{ marginTop: "1rem" }}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
